<template>
  <Dialog v-model:open="isOpen">
    <DialogContent :class="isMobile ? 'w-screen h-screen max-w-none m-0 rounded-none' : 'sm:max-w-[700px] max-h-[85vh]'" class="overflow-hidden flex flex-col">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <Lightbulb class="h-5 w-5 text-orange-500" />
          Manage Lights
        </DialogTitle>
        <DialogDescription>
          Connect and manage your Philips Hue bridges and WLED devices
        </DialogDescription>
      </DialogHeader>

      <!-- Tabs for Hue and WLED -->
      <Tabs v-model="activeTab" class="flex-1 flex flex-col overflow-hidden">
        <TabsList class="grid w-full grid-cols-2">
          <TabsTrigger value="hue" class="flex items-center gap-2">
            <Lightbulb class="h-4 w-4" />
            Philips Hue
          </TabsTrigger>
          <TabsTrigger value="wled" class="flex items-center gap-2">
            <Cpu class="h-4 w-4" />
            WLED
          </TabsTrigger>
        </TabsList>

        <!-- Hue Tab -->
        <TabsContent value="hue" class="flex-1 overflow-hidden mt-4">
          <ScrollArea class="h-full">
            <div class="space-y-6 pr-4">
              <!-- Discover Button -->
              <div class="flex justify-between items-center">
                <p class="text-sm text-muted-foreground">
                  Find Hue bridges connected to your network
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  @click="discoverBridges"
                  :disabled="isDiscovering"
                >
                  <Search class="h-4 w-4 mr-2" :class="{ 'animate-spin': isDiscovering }" />
                  {{ isDiscovering ? 'Discovering...' : 'Find Bridges' }}
                </Button>
              </div>

              <!-- Leader Selection (if any connected bridges) -->
              <div v-if="connectedBridges.length > 0" class="bg-muted/50 rounded-lg p-4 space-y-3">
                <div class="flex items-center justify-between">
                  <div class="flex-1">
                    <p class="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Sync Leader
                    </p>
                    <!-- Standalone mode: always this device -->
                    <p v-if="isStandalone" class="text-sm font-medium mt-1">
                      This Device
                      <Badge variant="default" class="ml-2 text-xs">
                        Standalone
                      </Badge>
                    </p>
                    <!-- Online mode: show leader info -->
                    <p v-else-if="currentLeader" class="text-sm font-medium mt-1">
                      {{ currentLeader.device_name || 'Unknown Device' }}
                      <Badge v-if="isCurrentDeviceLeader" variant="default" class="ml-2 text-xs">
                        This Device
                      </Badge>
                    </p>
                    <p v-else class="text-sm text-muted-foreground mt-1">
                      No leader selected
                    </p>
                  </div>
                  <Button
                    v-if="!isStandalone"
                    @click="handleSetAsLeader"
                    variant="outline"
                    size="sm"
                    :disabled="isCurrentDeviceLeader || isSettingLeader || connectedBridges.length === 0"
                  >
                    <span v-if="isSettingLeader">Setting...</span>
                    <span v-else-if="isCurrentDeviceLeader">Already Leader</span>
                    <span v-else>Sync from this Device</span>
                  </Button>
                </div>
                <p class="text-xs text-muted-foreground">
                  {{ isStandalone ? 'Standalone mode: This device directly controls the Hue bridge.' : 'Only the leader device writes changes to the Hue bridge. Other devices receive updates in real-time.' }}
                </p>
              </div>

              <!-- Connected Bridges -->
              <div v-if="connectedBridges.length > 0" class="space-y-2">
                <h4 class="text-sm font-semibold">Connected Bridges</h4>
                <div class="space-y-2">
                  <div
                    v-for="bridge in connectedBridges"
                    :key="`connected-${bridge.id}`"
                    class="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div class="flex items-start justify-between">
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2 mb-1">
                          <Lightbulb class="h-4 w-4 text-orange-500 flex-shrink-0" />
                          <h3 class="font-medium truncate">{{ bridge.name || 'Hue Bridge' }}</h3>
                          <Badge variant="secondary" class="ml-auto">Connected</Badge>
                        </div>
                        <p class="text-xs text-muted-foreground mb-1">{{ bridge.ip_address }}</p>
                        <p class="text-xs text-muted-foreground">
                          {{ getLightCount(bridge.bridge_id) }} light{{ getLightCount(bridge.bridge_id) !== 1 ? 's' : '' }}
                        </p>
                      </div>
                      <div class="flex gap-2 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          @click="viewBridge(bridge)"
                        >
                          <Eye class="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          @click="removeBridge(bridge)"
                          class="text-destructive hover:text-destructive"
                        >
                          <Trash2 class="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Discovered Bridges (not yet connected) -->
              <div v-if="undiscoveredBridges.length > 0" class="space-y-2">
                <h4 class="text-sm font-semibold">Discovered Bridges</h4>
                <div class="space-y-2">
                  <div
                    v-for="bridge in undiscoveredBridges"
                    :key="`discovered-${bridge.id}`"
                    class="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div class="flex items-start justify-between">
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2 mb-1">
                          <Lightbulb class="h-4 w-4 text-orange-500/50 flex-shrink-0" />
                          <h3 class="font-medium truncate">{{ bridge.name || 'Hue Bridge' }}</h3>
                        </div>
                        <p class="text-xs text-muted-foreground">{{ bridge.internalipaddress }}</p>
                      </div>
                      <Button
                        size="sm"
                        @click="connectBridge(bridge)"
                        :disabled="connectingBridgeId === bridge.id"
                      >
                        {{ connectingBridgeId === bridge.id ? 'Connecting...' : 'Connect' }}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Bridges Found -->
              <div
                v-if="connectedBridges.length === 0 && undiscoveredBridges.length === 0 && !isDiscovering"
                class="text-center py-12 text-muted-foreground"
              >
                <Lightbulb class="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p class="font-medium mb-1">No Hue bridges found</p>
                <p class="text-sm">Make sure your bridge is connected to the same network</p>
                <Button variant="outline" class="mt-4" @click="discoverBridges">
                  <Search class="h-4 w-4 mr-2" />
                  Search Again
                </Button>
              </div>

              <!-- Error Display -->
              <div v-if="error" class="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p class="text-sm text-destructive">{{ error }}</p>
                <Button
                  variant="outline"
                  size="sm"
                  @click="clearError"
                  class="mt-2"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </ScrollArea>
        </TabsContent>

        <!-- WLED Tab -->
        <TabsContent value="wled" class="flex-1 overflow-hidden mt-4">
          <ScrollArea class="h-full">
            <div class="pr-4">
              <WledDeviceSection />
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>

      <DialogFooter>
        <Button variant="ghost" @click="isOpen = false">
          Close
        </Button>
      </DialogFooter>
    </DialogContent>

    <!-- Bridge Viewer Modal (nested) -->
    <Dialog v-model:open="isBridgeViewerOpen">
      <DialogContent class="sm:max-w-[800px] max-h-[85vh]">
        <HueBridgeViewer
          v-if="viewingBridge"
          :bridge="viewingBridge"
          @close="isBridgeViewerOpen = false"
        />
      </DialogContent>
    </Dialog>
  </Dialog>
</template>

<script setup lang="ts">
import { logger } from '@/lib/logger'
import { ref, computed, watch } from 'vue'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Lightbulb, Search, Eye, Trash2, Cpu } from 'lucide-vue-next'
import HueBridgeViewer from './HueBridgeViewer.vue'
import WledDeviceSection from './WledDeviceSection.vue'
import { useHueBridgesStore, type HueBridge } from '@/stores/hueBridges'
import type { DiscoveredBridge } from '@/services/hue/HueDiscoveryService'
import { useLightsStore } from '@/stores/lights'
import { useBreakpoints } from '@/composables/useBreakpoints'
import { storeToRefs } from 'pinia'
import { isStandaloneMode } from '@/lib/dataProvider'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

// Stores
const hueBridgesStore = useHueBridgesStore()
const lightsStore = useLightsStore()
const { isMobile } = useBreakpoints()

const { 
  allBridges,
  availableDiscovered,
  isDiscovering,
  error 
} = storeToRefs(hueBridgesStore)

const { allLights } = storeToRefs(lightsStore)

// Local state
const activeTab = ref('hue')
const viewingBridge = ref<HueBridge | null>(null)
const isBridgeViewerOpen = ref(false)
const connectingBridgeId = ref<string | null>(null)
const isSettingLeader = ref(false)
const currentLeader = ref<any>(null)
const deviceSessionId = ref<string | null>(null)

const isOpen = computed({
  get: () => props.open,
  set: (value) => emit('update:open', value)
})

// Computed
const connectedBridges = computed(() => {
  return allBridges.value.filter(bridge => bridge.is_active)
})

const undiscoveredBridges = computed(() => {
  const connectedBridgeIps = new Set(connectedBridges.value.map(b => b.ip_address))
  return availableDiscovered.value.filter(bridge => 
    !connectedBridgeIps.has(bridge.internalipaddress)
  )
})

const isStandalone = computed(() => isStandaloneMode())

const isCurrentDeviceLeader = computed(() => {
  // In standalone mode, this device is always the leader
  if (isStandalone.value) return true
  if (!currentLeader.value || !deviceSessionId.value) return false
  return currentLeader.value.session_id === deviceSessionId.value
})

const getLightCount = (bridgeId: string) => {
  return allLights.value.filter(light => light.hue_bridge_id === bridgeId).length
}

// Methods
const discoverBridges = async () => {
  await hueBridgesStore.discoverBridges()
}

const connectBridge = async (bridge: HueBridge | DiscoveredBridge) => {
  try {
    connectingBridgeId.value = bridge.id
    
    const bridgeToConnect: DiscoveredBridge = 'internalipaddress' in bridge 
      ? bridge 
      : {
          id: bridge.id,
          internalipaddress: (bridge as HueBridge).ip_address,
          name: bridge.name || 'Hue Bridge',
          discoveryMethod: 'manual' as const,
          lastSeen: Date.now()
        }
    
    const success = await hueBridgesStore.connectToBridge(bridgeToConnect)
    
    if (success) {
      logger.info('Bridge connected successfully!')
    }
  } catch (err) {
    logger.error('Failed to connect bridge:', err)
  } finally {
    connectingBridgeId.value = null
  }
}

const viewBridge = (bridge: HueBridge) => {
  viewingBridge.value = bridge
  isBridgeViewerOpen.value = true
}

const removeBridge = async (bridge: HueBridge) => {
  if (confirm(`Remove bridge "${bridge.name}"? This will delete all associated lights and scenes.`)) {
    await hueBridgesStore.removeBridge(bridge.bridge_id || bridge.id)
  }
}

const clearError = () => {
  hueBridgesStore.clearError()
}

const handleSetAsLeader = async () => {
  const primaryBridge = connectedBridges.value[0]
  if (!primaryBridge) return
  
  try {
    isSettingLeader.value = true
    const success = await hueBridgesStore.setAsLeader(primaryBridge.bridge_id)
    if (success) {
      await loadLeaderInfo()
    }
  } catch (err) {
    logger.error('Failed to set as leader:', err)
  } finally {
    isSettingLeader.value = false
  }
}

const loadLeaderInfo = async () => {
  const primaryBridge = connectedBridges.value[0]
  if (!primaryBridge) return
  
  try {
    currentLeader.value = await hueBridgesStore.getActiveLeader(primaryBridge.bridge_id)
    
    const { hueSyncCoordinator } = await import('@/services/hue/HueSyncCoordinator')
    deviceSessionId.value = hueSyncCoordinator.getSessionId()
  } catch (err) {
    logger.error('Failed to load leader info:', err)
  }
}

// Watch for modal open/close
watch(isOpen, async (open) => {
  if (open) {
    await hueBridgesStore.fetchBridges()
    if (allLights.value.length === 0) {
      await lightsStore.fetchLights()
    }
    if (connectedBridges.value.length > 0) {
      await loadLeaderInfo()
    }
  }
})

// Watch for connected bridges changes
watch(connectedBridges, async (newBridges) => {
  if (newBridges.length > 0) {
    await loadLeaderInfo()
  }
}, { immediate: true })
</script>
